/**
 * chart-theme.ts — shared chart theming utilities for DCVFD dashboard
 *
 * Provides theme-aware colors for Recharts elements (axes, gridlines,
 * tooltips, legends) that work correctly in both light and dark modes.
 *
 * Usage:
 *   const { resolvedTheme } = useTheme();
 *   const chart = getChartTheme(resolvedTheme === 'dark');
 */

import type { CSSProperties } from "react";

export interface ChartTheme {
  /** Gridline stroke color */
  gridStroke: string;
  /** Axis line stroke color */
  axisStroke: string;
  /** Tick label fill color */
  tickColor: string;
  /** General label/legend text color */
  labelColor: string;
  /** Tooltip container background */
  tooltipBg: string;
  /** Tooltip container border color */
  tooltipBorder: string;
  /** Tooltip text color */
  tooltipText: string;
  /** Semi-transparent hover cursor fill */
  cursorFill: string;
}

export function getChartTheme(isDark: boolean): ChartTheme {
  return {
    gridStroke: isDark ? "#27272a" : "#e4e4e7", // zinc-800 / zinc-200
    axisStroke: isDark ? "#3f3f46" : "#a1a1aa", // zinc-700 / zinc-400
    tickColor: isDark ? "#9ca3af" : "#6b7280", // gray-400 / gray-500
    labelColor: isDark ? "#d4d4d8" : "#27272a", // zinc-300 / zinc-800
    tooltipBg: isDark ? "rgba(24,24,27,0.95)" : "rgba(255,255,255,0.98)",
    tooltipBorder: isDark ? "#3f3f46" : "#e4e4e7",
    tooltipText: isDark ? "#fafafa" : "#18181b",
    cursorFill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
  };
}

/**
 * Returns a Recharts-compatible contentStyle object for Tooltip.
 * Apply as: <Tooltip contentStyle={getTooltipStyle(isDark)} />
 */
export function getTooltipStyle(isDark: boolean): CSSProperties {
  const theme = getChartTheme(isDark);
  return {
    backgroundColor: theme.tooltipBg,
    border: `1px solid ${theme.tooltipBorder}`,
    color: theme.tooltipText,
    borderRadius: 8,
    fontSize: 12,
  };
}

/**
 * Standard data colors that work on both light and dark backgrounds.
 * Use these instead of dark brand green (#0E403A) for chart bars in dark mode.
 */
export const CHART_COLORS = {
  primary: "#16A34A", // bright green — good contrast both modes
  secondary: "#2563EB", // bright blue
  accent: "#D97706", // bright amber
  danger: "#DC2626", // bright red
  neutral: "#737373", // mid-gray — works both modes
  /** Brand-300 teal — use instead of dark brand green in dark mode bars */
  brandLight: "#4AADA0",
} as const;
